// Max number of allowed quote quantity slots
export const MAX_QUOTE_QUANTITIES = 10;

// Returns array of slot numbers from 1 up to MAX_QUOTE_QUANTITIES
export const quoteSlotNumbers = () =>
  Array.from({ length: MAX_QUOTE_QUANTITIES }, (_, index) => index + 1);

// Builds initial quote reference and percentage state for all slots
export const buildInitialQuoteRefState = () => {
  const state = {};
  quoteSlotNumbers().forEach((slot) => {
    state[`quoteRef${slot}`] = "$0.00";
    state[`percentageRef${slot}`] = "";
  });
  return state;
};

// Builds array of entered quote board sizes for API payload
export const buildQuotesPayload = (quoteData) =>
  quoteSlotNumbers().map((slot) => quoteData[`quote${slot}`]);

// Returns snapshot array of quote, ref, and percentage values for all slots
export const buildQuotesSnapshot = (quoteData, state) =>
  quoteSlotNumbers().map((slot) => ({
    [`quote${slot}`]: quoteData[`quote${slot}`],
    [`quoteRef${slot}`]: state[`quoteRef${slot}`],
    [`percentageRef${slot}`]: state[`percentageRef${slot}`],
  }));

// Returns array of filled quote board counts (finte/positive values only, as strings)
export const getFilledQuoteQuantities = (quoteData = {}) =>
  buildQuotesPayload(quoteData)
    .filter((value) => {
      if (value === "" || value == null) return false;
      const num = Number(value);
      return Number.isFinite(num) && num > 0;
    })
    .map((value) => String(value).trim());

// Column header/key for requested quantity
export const reqQtyColumn = (quoteQty) => `For Q ${quoteQty} - Req-Qty`;

// Column header/key for attrition quantity
export const attrQtyColumn = (quoteQty) => `For Q ${quoteQty} - Attr-Qty`;

// Checks if column is req or attrition quantity
export const isReqQtyColumn = (col) => /^For Q .+ - Req-Qty$/.test(col);
export const isAttrQtyColumn = (col) => /^For Q .+ - Attr-Qty$/.test(col);
export const isAttritionQtyColumn = (col) =>
  isReqQtyColumn(col) || isAttrQtyColumn(col);

// Build lookup object (lowercased package_case -> attrition obj)
export const buildAttritionPackageMap = (attritions = []) => {
  const map = {};
  attritions.forEach((item) => {
    const key = String(item.package_case || "")
      .trim()
      .toLowerCase();
    if (key) {
      map[key] = item;
    }
  });
  return map;
};

// Computes total qty needed for a line and quote count
export const computeReqQty = (lineQty, boards) => {
  const quantity = Number(lineQty);
  const quoteQty = Number(boards);
  if (!Number.isFinite(quantity) || !Number.isFinite(quoteQty)) {
    return 0;
  }
  return quantity * quoteQty;
};

/**
 * Calculates the recommended attrition/spares (takes higher of % or count)
 */
export const computeHigherAttrition = (reqQty, attrition) => {
  if (!attrition) {
    return 0;
  }

  const required = Number(reqQty) || 0;
  const percentage = Number(attrition.percentage) || 0;
  const count = Number(attrition.count) || 0;
  const percentageAmount = Math.ceil((required * percentage) / 100);

  return Math.max(percentageAmount, count);
};

/**
 * Returns Req-Qty + higher attrition amount
 */
export const computeAttrQty = (reqQty, attrition) => {
  const required = Number(reqQty) || 0;
  return required + computeHigherAttrition(required, attrition);
};

// Removes attrition-related columns from BOM line object
export const stripAttritionQtyColumns = (line) => {
  const next = { ...line };
  Object.keys(next).forEach((key) => {
    if (isAttritionQtyColumn(key)) {
      delete next[key];
    }
  });
  return next;
};

// Returns true if object is a real BOM line (has part number, not a section/total row)
export const isRealBomLine = (line) => {
  if (!line || typeof line !== "object") return false;
  if (line._section || line._section_title) return false;
  const part = line.manufacturer_part_number;
  if (part == null || String(part).trim() === "") return false;
  const normalized = String(part).trim();
  if (
    normalized === "Surface Mount Components (SMT)" ||
    normalized === "Through Hole Components (THT)"
  ) {
    return false;
  }
  return true;
};

/**
 * Updates/creates all Req-Qty and Attr-Qty columns for each quote board quantity.
 * If preserveAttrEdits is true, keeps user-edited Attr-Qty values.
 * Skips invalid/blank BOM lines.
 */
export const applyAttritionQtyColumns = (
  bomlines,
  quoteData,
  attritionByPackage = {},
  { preserveAttrEdits = false } = {}
) => {
  const quotes = getFilledQuoteQuantities(quoteData);

  return (bomlines || []).map((line) => {
    const previousAttrValues = {};
    if (preserveAttrEdits) {
      Object.keys(line || {}).forEach((key) => {
        if (isAttrQtyColumn(key)) {
          previousAttrValues[key] = line[key];
        }
      });
    }

    const next = stripAttritionQtyColumns(line);

    if (!quotes.length || !isRealBomLine(next)) {
      return next;
    }

    const packageKey = String(next.Package || "")
      .trim()
      .toLowerCase();
    const attrition =
      packageKey && packageKey !== "n/a"
        ? attritionByPackage[packageKey]
        : null;

    quotes.forEach((quoteQty) => {
      const req = computeReqQty(next.quantity, quoteQty);
      const reqCol = reqQtyColumn(quoteQty);
      const attrCol = attrQtyColumn(quoteQty);
      next[reqCol] = req;

      if (
        preserveAttrEdits &&
        previousAttrValues[attrCol] !== undefined &&
        previousAttrValues[attrCol] !== ""
      ) {
        next[attrCol] = previousAttrValues[attrCol];
      } else {
        next[attrCol] = computeHigherAttrition(req, attrition);
      }
    });

    return next;
  });
};

/**
 * Packed index of a sheet row inside the real-line list sent to Auto Quote.
 * Blank part numbers are omitted, matching LineQuoteAlignment on the API.
 */
export const packedQuoteIndexFor = (bomlines, lineIndex) => {
  const lines = bomlines || [];
  const hasIndexes = lines.some((line) => line && line._bomLineIndex != null);
  if (hasIndexes) {
    const ordered = lines
      .filter(isRealBomLine)
      .sort((a, b) => (a._bomLineIndex || 0) - (b._bomLineIndex || 0));
    const found = ordered.findIndex((line) => line._bomLineIndex === lineIndex);
    return found >= 0 ? found : null;
  }

  let packed = 0;
  for (let i = 0; i < lines.length; i += 1) {
    if (!isRealBomLine(lines[i])) {
      continue;
    }
    if (i === lineIndex) {
      return packed;
    }
    packed += 1;
  }
  return null;
};

/**
 * For each real BOM line, builds map {quoteQty: Req-Qty + Attr-Qty}.
 * Recomputes missing values as needed.
 * When lines carry _bomLineIndex (after SMT/THT organize), entries are
 * sorted back to original import order. The array is packed: rows with a
 * blank part number are left out, and the API maps that list back onto
 * saved parts by skipping those same rows.
 */
export const buildLineQuoteQuantities = (bomlines, quotes) => {
  const realLines = (bomlines || []).filter(isRealBomLine);
  const ordered = realLines.some((line) => line._bomLineIndex != null)
    ? [...realLines].sort(
        (a, b) => (a._bomLineIndex || 0) - (b._bomLineIndex || 0)
      )
    : realLines;

  return ordered.map((line) => {
    const entry = {};
    (quotes || []).forEach((quoteQty) => {
      const reqCol = reqQtyColumn(quoteQty);
      const attrCol = attrQtyColumn(quoteQty);
      const req = Number(line[reqCol]);
      const attr = Number(line[attrCol]);
      const reqValue = Number.isFinite(req)
        ? req
        : computeReqQty(line.quantity, quoteQty);
      const attrValue = Number.isFinite(attr) ? attr : 0;
      entry[String(quoteQty)] = reqValue + attrValue;
    });
    return entry;
  });
};

/** Per-line attrition breakdown for BOM persistence / history. */
export const buildLineAttritionDetails = (bomlines, quotes) => {
  const realLines = (bomlines || []).filter(isRealBomLine);
  const ordered = realLines.some((line) => line._bomLineIndex != null)
    ? [...realLines].sort(
        (a, b) => (a._bomLineIndex || 0) - (b._bomLineIndex || 0)
      )
    : realLines;

  return ordered.map((line) => {
    const entry = {};
    (quotes || []).forEach((quoteQty) => {
      const reqCol = reqQtyColumn(quoteQty);
      const attrCol = attrQtyColumn(quoteQty);
      const req = Number(line[reqCol]);
      const attr = Number(line[attrCol]);
      const reqValue = Number.isFinite(req)
        ? req
        : computeReqQty(line.quantity, quoteQty);
      const attrValue = Number.isFinite(attr) ? attr : 0;
      entry[String(quoteQty)] = {
        req_qty: reqValue,
        attr_qty: attrValue,
        quote_qty: reqValue + attrValue,
      };
    });
    if (line.part_id != null && line.part_id !== "") {
      entry.part_id = Number(line.part_id);
    }
    return entry;
  });
};

export const parseQuoteFromAttrColumn = (col) => {
  const match = String(col || "").match(/^For Q (.+) - Attr-Qty$/);
  return match ? match[1] : null;
};

/** Set QuoteQty N = Req + Attr for each filled quote (slot order). */
export const syncQuoteQtyFromAttrition = (line, quotes) => {
  const next = { ...line };
  (quotes || []).forEach((quoteQty, index) => {
    const req = Number(next[reqQtyColumn(quoteQty)]);
    const attr = Number(next[attrQtyColumn(quoteQty)]);
    const reqValue = Number.isFinite(req)
      ? req
      : computeReqQty(next.quantity, quoteQty);
    const attrValue = Number.isFinite(attr) ? attr : 0;
    next[`QuoteQty ${index + 1}`] = reqValue + attrValue;
  });
  return next;
};

/**
 * Re-apply saved attrition details onto bomlines (history / BomPage).
 * lineAttritionDetails: [{ "10": { req_qty, attr_qty, quote_qty }, ... }, ...]
 * Uses _bomLineIndex when present so SMT/THT reordering still maps correctly.
 */
export const applySavedAttritionToBomlines = (
  bomlines,
  quotes = [],
  lineAttritionDetails = []
) => {
  const quoteList = (quotes || []).map((q) => String(q).trim()).filter(Boolean);
  if (!quoteList.length || !lineAttritionDetails?.length) {
    return bomlines;
  }

  const realLinesOrdered = (bomlines || [])
    .filter(isRealBomLine)
    .some((line) => line._bomLineIndex != null)
    ? [...(bomlines || []).filter(isRealBomLine)].sort(
        (a, b) => (a._bomLineIndex || 0) - (b._bomLineIndex || 0)
      )
    : null;

  let detailIndex = 0;
  return (bomlines || []).map((line) => {
    if (!isRealBomLine(line)) {
      return line;
    }

    let lookupIndex = detailIndex;
    if (line._bomLineIndex != null && realLinesOrdered) {
      lookupIndex = realLinesOrdered.findIndex(
        (candidate) => candidate._bomLineIndex === line._bomLineIndex
      );
      if (lookupIndex < 0) {
        lookupIndex = detailIndex;
      }
    }
    const details = lineAttritionDetails[lookupIndex] || {};
    detailIndex += 1;
    const next = { ...line };

    quoteList.forEach((quoteQty, index) => {
      const detail = details[quoteQty] || details[String(quoteQty)] || {};
      const req =
        detail.req_qty != null
          ? Number(detail.req_qty)
          : computeReqQty(next.quantity, quoteQty);
      const attr = detail.attr_qty != null ? Number(detail.attr_qty) : 0;
      const quoteQtyValue =
        detail.quote_qty != null ? Number(detail.quote_qty) : req + attr;

      next[reqQtyColumn(quoteQty)] = req;
      next[attrQtyColumn(quoteQty)] = attr;
      next[`QuoteQty ${index + 1}`] = quoteQtyValue;
    });

    return next;
  });
};
